import { useCallback, useMemo, useRef, useState } from 'react';
import { isArray, isBoolean, isEqual, isNil, isNumber, isString } from 'lodash';
// Import Types
import type { FilterModel, PageSizeType, Paging, Sorting, TableData } from 'types';
// Import Helpers
import { generateRequestFilterField } from 'services/Helpers';

export interface BaseRequestBody {
    Filters: FilterModel[];
    Pageing: Paging;
    Sorting: Sorting;
    SortingThen?: Sorting;
}

interface GenerateFiltersForBaseRequestBodyParams {
    defaultInputsDataList: AnyObject[];
    defaultFilters?: FilterModel[];
    filtersFields?: AnyObject;
    shouldResetPage?: boolean;
}

export interface UseListFiltersReturnValue {
    isMounted: React.MutableRefObject<boolean>;
    baseRequestBody: BaseRequestBody;
    generateNewFilterField: typeof generateRequestFilterField;
    generateAndChangeFiltersForBaseRequestBody: (options: GenerateFiltersForBaseRequestBodyParams) => {
        tmpFilters: FilterModel[];
    };
    changeFilters: (filtersList: FilterModel[], shouldApplyToBaseRequestBody?: boolean) => void;
    changePageNumber: (pageNumber: number) => void;
    changePageSize: (pageSize: PageSizeType) => void;
    changeSorting: (sortingDirection: Sorting['Direction'], columnName?: string, columnId?: number) => void;
}

export const useListFilters = (filtersInitialConfig = {} as TableData): UseListFiltersReturnValue => {
    const {
        filters: _filters,
        paging: _paging,
        sorting: _sorting,
        sortingThen: _sortingThen,
    } = filtersInitialConfig.tableData;

    const isMounted = useRef(false);

    const [filters, setFilters] = useState<BaseRequestBody['Filters']>(_filters);
    const [paging, setPaging] = useState<BaseRequestBody['Pageing']>(_paging);
    const [sorting, setSorting] = useState<BaseRequestBody['Sorting']>(_sorting);
    const [sortingThen, setSortingThen] = useState<BaseRequestBody['SortingThen']>(_sortingThen);

    const baseRequestBody: BaseRequestBody = useMemo(
        () => ({
            Filters: filters,
            Pageing: paging,
            Sorting: sorting,
            SortingThen: sortingThen,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(filters), JSON.stringify(paging), JSON.stringify(sorting), JSON.stringify(sortingThen)],
    );

    const changeFilters = useCallback(
        (filtersList: FilterModel[], shouldApplyToBaseRequestBody = true) => {
            if (!isArray(filtersList)) return;
            if (shouldApplyToBaseRequestBody) {
                if (!isEqual(filters, filtersList)) {
                    setFilters(filtersList);
                }
            }
        },
        [filters],
    );

    const changePageNumber = useCallback((pageNumber: number) => {
        if (!isNumber(pageNumber)) return;
        setPaging((prev) => ({
            ...prev,
            PageNumber: pageNumber,
        }));
    }, []);

    const changePageSize = useCallback((pageSize: PageSizeType) => {
        if (!isNumber(pageSize) && !isString(pageSize)) return;
        if (pageSize === 'all') {
            pageSize = null;
        }
        setPaging((prev) => ({
            ...prev,
            PageSize: pageSize as Nullable<number>,
        }));
    }, []);

    const changeSorting = useCallback(
        (sortingDirection: Sorting['Direction'], columnName?: string, columnId?: number) => {
            if (!isBoolean(sortingDirection) && !isString(columnName)) {
                return;
            }
            setSorting((prev) => ({
                ...prev,
                Direction: sortingDirection,
                ...(isNil(columnId) ? { Name: columnName } : { Id: columnId }),
            }));
        },
        [],
    );
    const changeSortingThen = useCallback(
        (sortingThenDirection: Sorting['Direction'], columnName?: string, columnId?: number) => {
            if (!isBoolean(sortingThenDirection) && !isString(columnName)) {
                return;
            }
            setSortingThen((prev) => ({
                ...prev,
                Direction: sortingThenDirection,
                ...(isNil(columnId) ? { Name: columnName } : { Id: columnId }),
            }));
        },
        [],
    );

    const generateAndChangeFiltersForBaseRequestBody = useCallback(
        (options: GenerateFiltersForBaseRequestBodyParams): { tmpFilters: FilterModel[] } => {
            const { defaultInputsDataList, defaultFilters = [], filtersFields = {}, shouldResetPage = true } = options;
            const tmpFilters: FilterModel[] = [..._filters, ...defaultFilters];

            defaultInputsDataList.forEach(({ valueFieldKey, comparision }) => {
                const foundInitialFilterIndex = _filters.findIndex((filter) => filter?.Name === valueFieldKey);

                const newFilterData = generateRequestFilterField(
                    valueFieldKey,
                    comparision,
                    filtersFields[valueFieldKey],
                );

                if (foundInitialFilterIndex > -1) {
                    tmpFilters[foundInitialFilterIndex] = newFilterData;
                } else {
                    tmpFilters.push(newFilterData);
                }
            });

            changeFilters(tmpFilters);

            if (shouldResetPage) {
                changePageNumber(1);
            }

            return { tmpFilters };
        },
        [_filters, changeFilters, changePageNumber],
    );

    return useMemo(
        () => ({
            isMounted,
            baseRequestBody,
            generateNewFilterField: generateRequestFilterField,
            generateAndChangeFiltersForBaseRequestBody,
            changeFilters,
            changePageNumber,
            changePageSize,
            changeSorting,
            changeSortingThen,
        }),
        [
            baseRequestBody,
            generateAndChangeFiltersForBaseRequestBody,
            changeFilters,
            changePageNumber,
            changePageSize,
            changeSorting,
            changeSortingThen,
        ],
    );
};
